import { useState, useCallback } from "react";
import styles from "./DetailedProfile.module.css";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  useFollowUserMutation,
  useGetFollowCountQuery,
  useUnfollowUserMutation,
} from "../store/api/followApi";
import { useEditProfileMutation } from "../store/api/profileApi";
import { useSelector } from "react-redux";
import Modal from "./Modal";
import EditProfileForm from "./EditProfileForm";
import { toast } from "react-toastify";
import ImagePreview from "../components/common/ImagePreview";
import { useLogoutUserMutation } from "../store/api/authApi";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaVenusMars,
  FaCalendarAlt,
  FaTag,
  FaSearch,
} from "react-icons/fa";

export default function DetailedProfile({ user, isMyProfile }) {
  const [logoutUser] = useLogoutUserMutation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const currentUserId =
    useSelector((state) => state.auth.userId) || localStorage.getItem("userId");
  const profileUserId = isMyProfile ? currentUserId : user.id;

  const [followUser, { isLoading: isFollowing }] = useFollowUserMutation();
  const [unfollowUser, { isLoading: isUnfollowing }] =
    useUnfollowUserMutation();
  const [editProfile, { isLoading: isEditingProfile }] =
    useEditProfileMutation();

  const { data: followCount } = useGetFollowCountQuery(profileUserId);

  const handleFollow = async () => {
    try {
      await followUser(profileUserId).unwrap();
    } catch (err) {
      console.error("Follow failed", err);
    }
  };

  const handleUnfollow = async () => {
    try {
      await unfollowUser(profileUserId).unwrap();
    } catch (err) {
      console.error("Unfollow failed", err);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser().unwrap();
    } catch (err) {
      console.error("Logout API failed", err);
    }
    logout();
    navigate("/");
  };

  const openEditModal = useCallback(() => setIsEditModalOpen(true), []);
  const closeEditModal = useCallback(() => setIsEditModalOpen(false), []);

  const handleEditSubmit = async (formData) => {
    try {
      await editProfile(formData).unwrap();
      closeEditModal();
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error(
        error?.data?.message || "Failed to update profile. Please try again.",
      );
    }
  };

  const handleMessageClick = () => {
    navigate(`/messages/${user.id}`, {
      state: { userName: user.name, userPhoto: user.profilePhotoUrl },
    });
  };

  const dob = user.dateOfBirth
    ? new Date(user.dateOfBirth).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  const location = [user.city, user.state].filter(Boolean).join(", ") || null;

  const displayFollowers = followCount?.followers ?? 0;
  const displayFollowing = followCount?.following ?? 0;
  const isUserFollowing = followCount?.isFollowing ?? false;

  const userTypeLabel =
    user.userType === "Rookie" && user.rookieType
      ? `${user.userType} · ${user.rookieType}`
      : user.userType || null;

  const initials = user.name
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const detailRows = [
    { icon: <FaEnvelope />, label: "Email", value: user.email },
    {
      icon: <FaPhone />,
      label: "Phone",
      value:
        user.contactNumber && user.contactNumber !== "null"
          ? user.contactNumber
          : null,
    },
    { icon: <FaMapMarkerAlt />, label: "Location", value: location },
    { icon: <FaVenusMars />, label: "Gender", value: user.gender },
    { icon: <FaCalendarAlt />, label: "Birthday", value: dob },
    ...(user.interestedFields
      ? [
          {
            icon: <FaTag />,
            label: "Interested Fields",
            value: user.interestedFields.split(",").map((f) => f.trim()),
          },
        ]
      : []),
    ...(user.lookingFor
      ? [
          {
            icon: <FaSearch />,
            label: "Looking For",
            value: user.lookingFor.split(",").map((f) => f.trim()),
          },
        ]
      : []),
  ].filter((r) => r.value);

  return (
    <>
      <div className={styles.profileContainer}>
        {/* Photo */}
        <div className={styles.photoSection}>
          {user?.profilePhotoUrl ? (
            <ImagePreview
              src={user.profilePhotoUrl}
              alt={user?.name || "User"}
              className={styles.photo}
            />
          ) : (
            <div className={styles.noPhoto}>{initials || "?"}</div>
          )}
        </div>

        {/* Info */}
        <div className={styles.infoSection}>
          <h1 className={styles.name}>{user.name || "No Name"}</h1>

          {userTypeLabel && (
            <span className={styles.userTypeBadge}>{userTypeLabel}</span>
          )}

          {/* Bio — prominent at the top */}
          {/* <div className={styles.bioBlock}>
            {user.bio ? (
              <p className={styles.bioText}>{user.bio}</p>
            ) : (
              <p className={styles.bioPlaceholder}>No bio added yet.</p>
            )}
          </div> */}

          {/* Stats */}
          <div className={styles.statsContainer}>
            <div
              className={styles.statCard}
              onClick={() => navigate(`/profile/${profileUserId}/followers`)}
            >
              <span className={styles.statNumber}>{displayFollowers}</span>
              <span className={styles.statLabel}>Followers</span>
            </div>
            <div
              className={styles.statCard}
              onClick={() => navigate(`/profile/${profileUserId}/following`)}
            >
              <span className={styles.statNumber}>{displayFollowing}</span>
              <span className={styles.statLabel}>Following</span>
            </div>
          </div>

          <hr className={styles.divider} />

          {/* Detail rows */}
          {detailRows.length > 0 && (
            <div className={styles.detailList}>
              {detailRows.map(({ icon, label, value }) => (
                <div key={label} className={styles.detailRow}>
                  <span className={styles.detailIcon}>{icon}</span>
                  <div className={styles.detailContent}>
                    <span className={styles.detailLabel}>{label}</span>
                    {Array.isArray(value) ? (
                      <div className={styles.tagContainer}>
                        {value.map((item, index) => (
                          <span key={index} className={styles.tag}>
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={styles.detailValue}>{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        {!isMyProfile && (
          <div className={styles.actions}>
            {isUserFollowing ? (
              <button
                className={styles.unfollowBtn}
                onClick={handleUnfollow}
                disabled={isUnfollowing}
              >
                {isUnfollowing ? "Unfollowing…" : "Unfollow"}
              </button>
            ) : (
              <button
                className={styles.followBtn}
                onClick={handleFollow}
                disabled={isFollowing}
              >
                {isFollowing ? "Following…" : "Follow"}
              </button>
            )}
            <button className={styles.messageBtn} onClick={handleMessageClick}>
              Message
            </button>
          </div>
        )}

        {isMyProfile && (
          <div className={styles.actions}>
            <button className={styles.editBtn} onClick={openEditModal}>
              Edit Profile
            </button>
            <button className={styles.logoutBtn} onClick={handleLogout}>
              Logout
            </button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title="Edit Profile"
        size="large"
      >
        <EditProfileForm
          user={user}
          onSubmit={handleEditSubmit}
          isSubmitting={isEditingProfile}
          onCancel={closeEditModal}
        />
      </Modal>
    </>
  );
}
